import {
  ActionType,
  ActionWidget,
  ActiveRecordsOperator,
  Condition,
  Expression,
  getEasyImportTemplate,
  http,
  IFormSubviewListFieldWidget,
  isRelation2MField,
  ModelCache,
  queryPage,
  RuntimeM2MField,
  RuntimeO2MField,
  SPI,
  SubmitCacheManager,
  ValidatorStatus,
  ViewActionTarget,
  Widget
} from '@kunlun/dependencies';
import { OioMessage, Optional, WidgetTrigger } from '@kunlun/vue-ui-antd';
import XLSX from 'xlsx';
import ImportCom from './FormTableImportActionWidget.vue';
import { queryWorkbook } from '../../../api';

@SPI.ClassFactory(
  ActionWidget.Token({
    widget: 'FormTableImport',
    actionType: [ActionType.View],
    target: [ViewActionTarget.Dialog, ViewActionTarget.Router, ViewActionTarget.Inner]
  })
)
export class FormTableImport extends ActionWidget {
  public initialize(config) {
    super.initialize(config);
    this.setComponent(ImportCom);
    return this;
  }

  @Widget.Reactive()
  protected get label() {
    return this.getDsl().label || this.action?.displayName || '导入';
  }

  @Widget.Reactive()
  private validation1: { status: ValidatorStatus; help: string } = {
    status: ValidatorStatus.Success,
    help: ''
  };

  @Widget.Reactive()
  private validation2: { status: ValidatorStatus; help: string } = {
    status: ValidatorStatus.Success,
    help: ''
  };

  private firstOpen = true;

  @Widget.Reactive()
  private visible = false;

  @Widget.Reactive()
  private selectValue = '';

  @Widget.Reactive()
  private workbookList: any[] = [];

  @Widget.Reactive()
  private handleSelectChange(val) {
    this.selectValue = val;
  }

  public clickAction() {
    this.change(true);
  }

  @Widget.Method()
  private async downloadTemplate() {
    if (!this.selectValue) {
      this.validation1.status = ValidatorStatus.Error;
      this.validation1.help = this.translate('kunlun.import.tips');
      return;
    }
    const urlObj: any = await getEasyImportTemplate(this.selectValue);
    window.open(urlObj.redirectUri);
  }

  @Widget.Method()
  private async change(val) {
    const importTemplateName = this.getConfig('importTemplateName');
    this.validation1 = {
      status: ValidatorStatus.Success,
      help: ''
    };
    this.validation2 = { status: ValidatorStatus.Success, help: '' };
    this.selectValue = '';
    if (this.firstOpen && val) {
      this.firstOpen = false;
      this.workbookList = (await queryWorkbook(this.model, false, importTemplateName as string)).content;
    }
    if (this.workbookList && this.workbookList.length > 0) {
      this.selectValue = this.workbookList[0].id;
    }
    this.visible = val;
  }

  private customSetBusy(loading: boolean) {
    typeof loading !== 'undefined' && (loading ? window.postMessage('open', window.location.href) : window.postMessage('close', window.location.href));
    this.loading = loading;
  }

  public getConfigDefault(key: string, defaultValue: string): string {
    const data = this.getConfig(key) as string;
    if (!data) {
      return defaultValue;
    }
    return data;
  }

  @Widget.Method()
  private async doImport() {
    if (!this.selectValue) {
      this.validation1.status = ValidatorStatus.Error;
      this.validation1.help = this.translate('kunlun.import.tips');
      return;
    }

    this.validation1.status = ValidatorStatus.Success;
    this.validation1.help = '';

    const num = this.getConfigDefault('nums', '');
    const pk = this.getConfigDefault('pks', '');
    const importFields = this.getConfigDefault('importFields', '');
    const queryDataStr = this.getConfigDefault('queryDataStr', '');
    const requiredFields = this.getConfigDefault('requiredFields', '');

    // variables="orderType:${rootRecord.orderType};currencyCode:${rootRecord.currencyCode};buyerPartnerId:${rootRecord.buyerPartnerId};excludeIds:${LIST_FIELD_VALUES(rootRecord.checkedNormalSkuIds,&apos;&apos;,&apos;id&apos;)}"
    const queryBody: Record<string, unknown> = {};

    if (queryDataStr) {
      const exp = Expression.getInstance();
      exp.initExpressionContext(this.activeRecords, this.rootData?.[0], {}, '');
      try {
        queryDataStr.split(';').forEach((f) => {
          const arr = f.split(':');
          if (arr.length > 1) {
            const key = arr[0];
            const valueStr = arr[1];
            try {
              const value = exp.exec(valueStr);
              if (value !== undefined) {
                queryBody[key] = value;
              }
            } catch (e) {
              console.error(e);
            }
          }
        });
      } finally {
        exp.cleanupExpressionContext();
      }
    }

    // 需要后端支持配置
    const maxLength = this.getConfigDefault('maxLength', '600');
    const importTips = this.getConfigDefault('importTips', '导入数据异常');
    const decimalLimit = this.getConfigDefault('decimalLimit', '');
    const patterns = this.getConfigDefault('patterns', '');

    if (!importFields) {
      throw new Error('importFields config is required');
    }

    const fieldToNameMap = {};
    const nameToFieldMap = {};
    const pks = pk.split(',');
    const nums = num.split(',');
    const coverFields: string[] = [];
    let importKey: any = [];

    const importResult: { [key: string]: any[] } = {};
    importFields.split(',').forEach((f) => {
      const arr = f.split(':');
      if (arr.length > 1) {
        nameToFieldMap[arr[0]] = arr[1];
        fieldToNameMap[arr[1]] = arr[0];
        if (!pks.includes(arr[1])) {
          coverFields.push(arr[1]);
        }
      }
    });

    const handleChangeFile = (e) => {
      try {
        this.customSetBusy(true);
        const file = <File>e.target.files[0];
        const format = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
        const importFileFormat = ['xlsx', 'xls'];
        if (!importFileFormat.includes(format)) {
          OioMessage.error('仅支持上传excel文件');
        } else {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
            outputWorkbook(workbook);
          };
          reader.readAsBinaryString(file);
        }
        // 需要清除input的value，不然相同的文件无法上传多次
        if (input.value) {
          input.value = '';
        }
      } catch (error) {
        this.customSetBusy(false);
      }
    };

    const outputWorkbook = (workbook: XLSX.WorkBook) => {
      const sheetNames = workbook.SheetNames;
      const worksheet = workbook.Sheets[sheetNames[0]];
      const sheetList = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false }) as any[];
      checkoutSheetList(sheetList);
    };

    const checkoutSheetList = async (sheetList: any[]) => {
      if (!sheetList.length) {
        OioMessage.error('当前无导入数据明细，请检查数据');
        return this.customSetBusy(false);
      }
      if (sheetList.length > 600) {
        OioMessage.error('当前导入仅支持导入600行明细数据导入，请核对数据后重新导入');
        return this.customSetBusy(false);
      }
      let errorMsg = '';
      const pkDatas: { [key: string]: any[] } = {};
      for (let index = 0; index < sheetList.length; index++) {
        const sheet = sheetList[index];
        const item = {};
        const items = Object.keys(sheet);
        items.forEach((v, i) => {
          if (nameToFieldMap[v]) {
            item[nameToFieldMap[v]] = sheet[v];
          }
        });
        importKey = item;
        let error = true;
        for (let i = 0; i < pks.length; i++) {
          if (!item[pks[i]]) {
            break;
          }
          // 检测是否输入去重字段
          if (item[pks[i]]) {
            if (!importResult[pks[i]]) {
              importResult[pks[i]] = [item];
            } else {
              importResult[pks[i]].push(item);
            }
          }
          if (i === pks.length - 1) {
            error = false;
          }
          // 去重
          if (!pkDatas[pks[i]]) {
            pkDatas[pks[i]] = [];
          }
          if (pkDatas[pks[i]].includes(item[pks[i]])) {
            // 去重报错
            errorMsg = `第${index + 1}行${fieldToNameMap[pks[i]]}重复，导入文档请去重`;
            OioMessage.error(errorMsg);
            this.customSetBusy(false);
            return;
          }
          pkDatas[pks[i]].push(item[pks[i]]);
        }
        if (error) {
          errorMsg = `第${index + 1}行${pks.map((pk) => fieldToNameMap[pk]).join(this.translate('或者'))}`;
          OioMessage.error(errorMsg);
          this.customSetBusy(false);
          return;
        }

        // 必填字段校验
        if (requiredFields) {
          let requiredFieldsField;
          const requiredFieldsArr = requiredFields.split(',');
          for (let i = 0; i < requiredFieldsArr.length; i++) {
            requiredFieldsField = requiredFieldsArr[i];
            if (!item[requiredFieldsField] && fieldToNameMap[requiredFieldsField]) {
              errorMsg = `第${index + 1}行${fieldToNameMap[requiredFieldsField]}必填`;
              OioMessage.error(errorMsg);
              this.customSetBusy(false);
              return;
            }
          }
        }

        // 小数位数限制
        if (decimalLimit) {
          let decimalLimitItem;
          const decimalLimitArr = decimalLimit
            .split(';')
            .filter((i) => i)
            .map((i) => ({ name: i.split(':')[0], field: i.split(':')[1], limit: i.split(':')[2] }));
          for (let i = 0; i < decimalLimitArr.length; i++) {
            decimalLimitItem = decimalLimitArr[i];
            if (item[decimalLimitItem.field]) {
              const decimal = `${item[decimalLimitItem.field]}`.split('.')?.[1];
              if (decimal && decimal.length > decimalLimitItem.limit) {
                error = true;
                break;
              }
            }
          }
          if (error) {
            errorMsg = `第${index + 1}行${decimalLimitItem.name}小数位数不能超过${decimalLimitItem.limit}`;
          }
        }
        // 正则校验
        // let patterns = '申请售后数量（必填）:applyReverseNum:/^\d+\.\d{2}$/:必须为大于0的2位小数;';
        if (patterns) {
          let paternItem;
          const patternArr = patterns
            .split(';')
            .filter((i) => i)
            .map((i) => ({
              name: i.split(':')[0],
              field: i.split(':')[1],
              reg: eval(i.split(':')[2]),
              msg: i.split(':')[3]
            }));
          for (let i = 0; i < patternArr.length; i++) {
            paternItem = patternArr[i];
            if (!paternItem.reg.test(item[paternItem.field]?.trim())) {
              error = true;
              break;
            }
          }
          if (error) {
            errorMsg = `第${index + 1}行${paternItem.name}${paternItem.msg}`;
          }
        }
      }
      if (errorMsg) {
        OioMessage.error(errorMsg);
        this.customSetBusy(false);
      } else {
        let error = false;
        if (maxLength && importResult) {
          const maxLengthInt = Number(maxLength);
          Object.keys(importResult).forEach((pk) => {
            if (importResult[pk].length > maxLengthInt) {
              error = true;
              OioMessage.error(`一次性导入做多支持${maxLength}行`);
            }
          });
        }
        if (error) {
          return this.customSetBusy(false);
        }
        await setData(importResult);
      }
    };
    const setData = async (importResult) => {
      // 获取modelFields
      try {
        const model = await ModelCache.get(this.action.model!);
        if (model && model.modelFields) {
          const modelFields = model.modelFields as any[];
          Object.keys(importResult).forEach((pk) => {
            importResult[pk].forEach((e: any, i) => {
              modelFields.forEach((field) => {
                if (e && field.name && e[field.name]) {
                  if (field.ttype && field.ttype == 'STRING' && typeof e[field.name] !== 'string') {
                    e[field.name] = String(e[field.name]);
                  }
                  if (field.ttype && field.ttype == 'DATETIME' && typeof e[field.name] !== 'string') {
                    let date = new Date(e[field.name]);
                    date = new Date(date.getTime() + 43 * 1000);
                    const yy = date.getFullYear();
                    const mm = date.getMonth() + 1;
                    const dd = date.getDate();
                    const hh = date.getHours();
                    const m = date.getMinutes();
                    const ss = date.getSeconds();
                    e[field.name] = `${yy}-${mm}-${dd} ${hh}:${m}:${ss}`;
                  }
                }
              });
            });
          });
        }

        const pkCondition: any = [];
        let trueOrError = true;
        Object.keys(importResult).forEach((pk) => {
          importResult[pk].forEach((e: any, i) => {
            if (e[pk]) {
              let str = '';
              pks.forEach((item, index) => {
                str += `${index && e[item?.trim()] ? ' and ' : ''}${e[item?.trim()] ? item : ''} ${e[item?.trim()] ? ' =in= ' : ''}${
                  e[item?.trim()] ? `'${e[item?.trim()]}'` : `${e[item?.trim()]}`
                }`;
              });
              const newStr = str.split('undefined');
              pkCondition.push(newStr.join(''));
            } else {
              trueOrError = false;
            }
          });
        });
        let result: any;
        if (trueOrError) {
          try {
            const rsql = [pkCondition]
              .filter((c) => !!c)
              .map((c) => `(${c})`)
              .join(' and ');
            const condition = new Condition(rsql);
            condition.setConditionBodyData(queryBody);
            result = await queryPage(
              this.action.model!,
              {
                condition,
                pageSize: 1000
              },
              undefined,
              this.variables
            );
          } catch (error) {
            console.error(error, 'error');
          }
        } else {
          OioMessage.error('检测到信息未填写准确');
        }
        const errorData: any[] = [];
        const dataSource = this.dataSource!;

        const field = Optional.ofNullable(this.metadataRuntimeContext.field)
          .filter<RuntimeO2MField | RuntimeM2MField | undefined>((v) => isRelation2MField(v!))
          .orElse(undefined);
        const subviewFieldWidget = Optional.ofNullable(this.metadataRuntimeContext.handle)
          .map(Widget.select)
          .map<IFormSubviewListFieldWidget | undefined>((v) => v!.getParent() as unknown as IFormSubviewListFieldWidget)
          .orElse(undefined);
        if (!field || !subviewFieldWidget) {
          console.error('Invalid field and subview widget');
          return;
        }

        let isPushDataSource = false;

        /**
         * 思路，多pk字段情况，多个字段通过 分隔符，把value合并成一个字段，变成一个key
         */
        Object.keys(importResult).forEach((pk) => {
          const resultMap = new Map();
          if (result.content != undefined) {
            result.content.forEach((r) => {
              const ruPk: any = r[pk];
              const ruCode: any = r.code ? r.code : '';
              resultMap.set(ruPk + ruCode, r);
              resultMap.set(r[pk], r);
            });
          } else {
            errorData.push(pk);
          }
          importResult[pk].forEach((r) => {
            const ko = `${r[pk]}`;
            if (resultMap.get(ko)) {
              if (resultMap.get(ko + (r.code ? r.code?.trim() : ''))) {
                // 如果有r[pk] productOutCode
                const existItem = dataSource.find((v) => (r.code !== undefined ? v[pk] == ko && (v.code ? v.code : '') == (r.code ? r.code : '') : v[pk] == ko));
                if (existItem) {
                  nums.forEach((num) => {
                    !isNaN(existItem[num] as number) && // 如果商品总数!=0
                      !isNaN(r[num]) &&
                      (existItem[num] = String(Number(existItem[num]) + Number(r[num]?.trim()))); // 两个商品总数项加
                  });
                } else {
                  const inDateSource = [] as any[];
                  inDateSource.push({
                    ...r,
                    ...resultMap.get(ko),
                    ...coverFields.reduce(
                      (t, n) => ({
                        ...t,
                        [n]: nums.includes(n) ? (r[n]?.trim() !== '' ? r[n]?.trim() : 0) : r[n]?.trim()
                      }),
                      {}
                    )
                  });
                  coverFields.forEach((v) => {
                    if (!r[v]) {
                      inDateSource[0][v] = resultMap.get(ko)[v];
                    }
                  });
                  Object.keys(importKey).forEach((v) => {
                    inDateSource.forEach((item) => {
                      // 类型转换失败，赠品为布尔值
                      if (item[v] && typeof item[v] === 'string') {
                        if (item[v]?.trim() === '是') {
                          item[v] = true;
                        } else if (item[v]?.trim() === '否') {
                          item[v] = false;
                        }
                      }
                      if (!item[v]) {
                        delete item[v];
                      }
                    });
                  });
                  // 修改table具体值
                  const showRecords = subviewFieldWidget.dataSource;
                  const { submitCache } = field;
                  const subviewSubmitCache = this.metadataRuntimeContext.extendData.subviewSubmitCache as SubmitCacheManager;
                  if (showRecords) {
                    if (submitCache) {
                      ActiveRecordsOperator.operator(showRecords, submitCache).push(inDateSource);
                      // todo update
                    }
                    if (subviewSubmitCache) {
                      ActiveRecordsOperator.operator(showRecords, subviewSubmitCache).push(inDateSource);
                      // todo update
                    }
                    isPushDataSource = true;
                  }
                }
              } else {
                errorData.push(ko);
              }
            } else {
              errorData.push(ko);
            }
          });
        });

        if (isPushDataSource) {
          subviewFieldWidget.flushDataSource();
          subviewFieldWidget.afterTriggerExecute?.(WidgetTrigger.CHANGE);
        }

        if (errorData.length) {
          if (importTips) {
            OioMessage.error(`${pks.map((pk) => fieldToNameMap[pk]).join(this.translate('kunlun.o2mImport.or'))} ${errorData.join(', ')}  ${importTips}`);
          } else {
            OioMessage.error(
              this.translate('文件不存在', {
                key: pks.map((pk) => fieldToNameMap[pk]).join(this.translate('kunlun.o2mImport.or')),
                errorData: errorData.join(', ')
              })
            );
          }
        } else {
          this.visible = false;
        }
        this.customSetBusy(false);
      } catch (error) {
        console.error(error);
        this.customSetBusy(false);
      }
    };

    // 弹出选择文件弹窗
    const input = document.createElement('input');
    input.type = 'file';
    input.style.display = 'none';
    input.onchange = handleChangeFile;
    input.click();
  }
}
